<template>
  <v-container fluid class="page page-provider-connection-edit">

    <!-- Breadcrumb -->
    <v-row class="mt-4 mb-6">
      <v-col cols="12" class="d-flex align-center justify-start page-breadcrumb-new">
        <!-- (breadcrumb) -->
        <v-icon small class="mr-2">mdi-link</v-icon>
        <router-link :to="{ name: 'provider-connection-list' }">Shipper Accounts</router-link>
        <div class="separator">/</div>
        <div class="active-item">
          <span class="label-prefix">Manage {{ $root.providerAliasToName(providerAlias) }} Connection:</span>
          <span class="pl-1" v-if="itemToEdit">{{ itemToEdit.name }}</span>
        </div>
      </v-col>
    </v-row>

    <!-- Page Title and Close Action -->
    <v-row class="mt-0">
      <v-col cols="9">
        <v-toolbar>
          <v-toolbar-title class="page-title">Manage {{ $root.providerAliasToName(providerAlias) }} Connection</v-toolbar-title>
          <!-- (saved message) -->
          <div class="success-message-text ml-10" v-if="savedMessage">
            <v-icon color="green" class="mr-2 pb-1">mdi-check-bold</v-icon>
            <span>{{ savedMessage }}</span>
          </div>
        </v-toolbar>
      </v-col>
      <v-col cols="3" align="right" class="mt-6">
        <!-- (close action) -->
        <v-btn text color="primary" class="white--text" @click="cancel">
          Close
        </v-btn>
      </v-col>
    </v-row>

    <!-- (connection saving) -->
    <template v-if="isSaving">
      <v-row class="mt-4">
        <v-col>
          <v-progress-circular indeterminate :size="50" color="primary" />
        </v-col>
      </v-row>
    </template>

    <!-- Manage Form -->
    <div class="mt-4 page-content" v-else-if="itemToEdit">

      <!-- API Key -->
      <v-row no-gutters>
        <v-col>
          Provider API Key:
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col cols="12" xs="12" sm="7" md="6" lg="5">
          <v-text-field
              hide-details="auto"
              :append-icon="showApiKey ? 'mdi-eye' : 'mdi-eye-off'"
              :type="showApiKey ? 'text' : 'password'"
              @click:append="showApiKey = !showApiKey"
              @change="isKeyChanged = true"
              v-model="itemToEdit.api_key" />
        </v-col>
      </v-row>
      <!-- (connect button) -->
      <v-row class="mt-5" v-if="supportsConnect">
        <v-col cols="12" xs="12" sm="7" md="6" lg="5" align="center">
          <v-btn width="150" color="amber darken-3" @click="connectToProvider" :disabled="!isKeyChanged">
            Connect
          </v-btn>
        </v-col>
      </v-row>

      <!-- (connecting) -->
      <template v-if="isAccountDataLoading">
        <v-row class="mt-8">
          <v-col cols="12" xs="12" sm="7" md="6" lg="5" align="center">
            <v-progress-circular indeterminate :size="24" color="white" />
          </v-col>
        </v-row>
      </template>

      <!-- Account Management -->
      <template v-else>
        <!-- (error connecting message) -->
        <v-row class="mt-2" v-if="providerConnectionError">
          <v-col cols="12" xs="12" sm="7" md="6" lg="5" align="center" class="error-message-text">
            {{ providerConnectionError }}
          </v-col>
        </v-row>

        <!-- Selected Account -->
        <template v-if="loadedAccounts">
          <v-row class="mt-8">
            <v-col>
              Account:
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col cols="12" xs="12" sm="7" md="6" lg="5">
              <v-select
                  :items="loadedAccounts"
                  item-text="name"
                  item-value="thirdparty_id"
                  v-model="itemToEdit.thirdparty_account_id"
                  @change="loadBrands" />
            </v-col>
          </v-row>
        </template>

        <!-- Selected Brands -->
        <v-row class="mt-6 mb-8" v-if="isBrandDataLoading">
          <v-col cols="12" xs="12" sm="7" md="6" lg="5" align="center">
            <v-progress-circular indeterminate :size="24" color="white" />
          </v-col>
        </v-row>
        <template v-else-if="loadedBrands">
          <v-row class="mt-6 mb-1">
            <v-col>
              Brands:
            </v-col>
          </v-row>
          <v-row no-gutters v-for="(brand, index) in loadedBrands" :key="`brand-${index}`">
            <v-col cols="12" xs="12" sm="7" md="6" lg="5" class="ml-6">
              <v-checkbox
                  v-model="itemToEdit.settings.brands"
                  :value="brand.thirdparty_id"
                  :label="brand.name" />
            </v-col>
          </v-row>
        </template>

        <!-- Selected Statuses -->
        <template v-if="allowStatusConfiguration">
          <v-row class="mt-6 mb-1">
            <v-col>
              Include Status(es):
            </v-col>
          </v-row>
          <v-row no-gutters v-for="status in availableStatuses" :key="status">
            <v-col cols="12" xs="12" sm="7" md="6" lg="5" class="ml-6">
              <v-checkbox
                  v-model="itemToEdit.settings.status"
                  :value="status"
                  :label="status" />
            </v-col>
          </v-row>
        </template>

        <v-row class="mt-8 mb-1 ml-1">
          <v-col class="py-6 d-flex align-center justify-left status-container">
            <div class="pr-8">Connection Status:</div>
            <v-switch
                class="ma-0 pt-0"
                :hide-details="true"
                v-model="itemToEditIsActive"
                label="Active" />
          </v-col>
        </v-row>

      </template> <!-- END account management -->

    </div> <!-- END form --->

    <!-- Page Actions (cancel, save) -->
    <v-row class="mt-6 mb-4" v-if="!isSaving">
      <v-col cols="6" class="pl-8">
        <v-btn color="gray" width="100" @click="cancel">Cancel</v-btn>
      </v-col>
      <v-col cols="6" class="pr-8" align="right">
        <v-btn color="primary" width="110" @click="save">Save</v-btn>
      </v-col>
    </v-row>

  </v-container>

</template>

<script>
export default {
  name: 'ProviderConnectionEditPage',

  data () {
    return {
      allowStatusConfiguration: false,
      isSaving: false,
      savedMessage: null,
      providerSaveError: null,
      isKeyChanged: false,
      showApiKey: false,
      isAccountDataLoading: false,
      isBrandDataLoading: false,
      providerConnectionError: null,
      loadedAccounts: null,
      loadedBrands: null
    }
  },

  props: {
    connectionId: {
      type: String,
      required: true
    }
  },

  computed: {
    itemToEdit () {
      return this.$store.getters['sh/itemEditing']('providerConnection')
    },
    itemToEditIsActive: {
      get () {
        return this.itemToEdit ? this.itemToEdit.status === 'ACTIVE' : false
      },
      set (value) {
        value ? this.itemToEdit.status = 'ACTIVE' : this.itemToEdit.status = 'INACTIVE'
      }
    },
    providerAlias () {
      return (this.itemToEdit) ? this.itemToEdit.provider_alias : null
    },
    selectedAccount () {
      return (this.loadedAccounts && this.itemToEdit.thirdparty_account_id)
        ? this.loadedAccounts.find(item => item.thirdparty_id === this.itemToEdit.thirdparty_account_id)
        : null
    },
    supportsConnect () {
      return this.providerAlias === 'leaflink'
    },
    supportsAccounts () {
      return this.providerAlias === 'leaflink'
    },
    availableStatuses () {
      return ['submitted', 'accepted', 'fulfilled']
    }
  },

  async beforeMount () {
    // Ensure connection to edit is loaded
    if (!this.itemToEdit) {
      await this.loadConnectionToEdit()
    }

    // Load accounts and brands
    if (this.supportsAccounts) {
      await this.loadAccounts()
      await this.loadBrands()
    }
  },

  methods: {
    cancel () {
      // Return to list page
      this.goToProviderConnectionsListPage()
    },

    goToProviderConnectionsListPage () {
      this.$root.goTo('provider-connection-list')
    },

    autoHideMessage () {
      setTimeout(() => {
        this.savedMessage = null
        this.providerSaveError = null
      }, 4000)
    },

    async loadConnectionToEdit () {
      // this.isDataLoading = true

      try {
        // Retrieve connection
        const connection = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: `provider-connection/${this.connectionId}`
        })
        // Set to edit
        this.$store.dispatch('sh/setItemToEdit', connection)
      } catch (err) {
        console.log('Error loading providing connection:', err)
      } finally {
        // this.isDataLoading = false
      }
    },

    async save () {
      this.isSaving = true

      try {
        // Update name to selected account
        if (this.selectedAccount) this.itemToEdit.name = this.selectedAccount.name

        // Update connection
        const updatedConnection = await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `provider-connection/${this.connectionId}`,
          opts: {
            bodyData: this.itemToEdit
          }
        })

        // Sync update into store
        updatedConnection.account = this.itemToEdit.account
        updatedConnection.brands = this.itemToEdit.brands
        updatedConnection.shipper = this.itemToEdit.shipper
        this.$store.dispatch('sh/syncData', {
          resource: 'providerConnections',
          nested: 'items',
          data: updatedConnection
        })

        // Show saved message
        this.savedMessage = 'Saved'
        this.autoHideMessage()
      } catch (err) {
        this.providerSaveError = err.message
      }

      this.isSaving = false
    },

    async connectToProvider () {
      this.isKeyChanged = false
      this.providerConnectionError = null
      await this.loadAccounts()
    },

    // NOTE - This is for the future API endpoint - DO NOT REMOVE !!!
    // async loadAccountsFuture () {
    //   this.isAccountDataLoading = true
    //
    //   try {
    //     const accounts = await this.$store.dispatch('sh/dataApi', {
    //       method: 'get',
    //       uri: `provider-connection/${this.connectionId}/accounts`
    //     })
    //     if (accounts.items && accounts.items.length > 0) {
    //       this.loadedAccounts = accounts.items
    //     }
    //   } catch (err) {
    //     this.providerConnectionError = err.message || err.text || err
    //   } finally {
    //     this.isAccountDataLoading = false
    //   }
    // },

    // Load available accounts for the connection
    async loadAccounts () {
      this.isAccountDataLoading = true

      try {
        const accounts = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: 'provider/accounts',
          headers: { provider_api_key: this.itemToEdit.api_key },
          opts: {
            params: { provider: this.providerAlias }
          }
        })
        if (accounts.items && accounts.items.length > 0) {
          this.loadedAccounts = accounts.items
        }
      } catch (err) {
        const message = (err.status === 403) ? 'Invalid API Key' : err.message
        this.providerConnectionError = message
      } finally {
        this.isAccountDataLoading = false
      }
    },

    // Load brands for the selected account
    async loadBrands () {
      this.isBrandDataLoading = true

      try {
        const brands = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: 'provider/brands',
          headers: { provider_api_key: this.itemToEdit.api_key },
          opts: {
            params: { provider: this.providerAlias, account_id: this.itemToEdit.thirdparty_account_id }
          }
        })
        if (brands.items && brands.items.length > 0) {
          this.loadedBrands = brands.items
        }
      } catch (err) {
        console.log(`Error retrieving brands for provider ${this.providerAlias} =>`, err)
      }

      this.isBrandDataLoading = false
    }
  }
}
</script>

<style scoped>
  .page-provider-connection-edit .success-message-text {
    font-size: 1.23em;
    color: #4CAF50;
    font-weight: 400;
  }

  .page-provider-connection-edit .error-message-text {
    font-size: 15px;
    color: #cc0000;
    font-weight: 400;
  }

  .page-provider-connection-edit .status-container {
    border: 1px solid #525252;
    border-radius: 6px;
    max-width: 300px;
  }
</style>
